import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  activateTrainingPlanMaterialisation,
  confirmTrainingPlanDraft,
  fetchCurrentActiveTrainingPlan,
  fetchCurrentTrainingBlock,
  fetchCurrentTrainingPlanDraft,
  fetchCurrentTrainingPlanLifecycle,
  fetchTrainingBlockWeeks,
  generateTrainingPlan,
  materialiseTrainingPlanConfirmation,
  replaceActiveTrainingPlan,
} from "@/api/trainingPlanApi";
import { CoachingMetric, CoachingSectionCard } from "@/components/coaching/CoachingSectionCard";
import { SportBadge } from "@/components/SportBadge";
import { Button } from "@/components/ui/button";
import { describeMissingInformation } from "@/lib/missingInformationCopy";
import { prescriptionIntensityLabel } from "@/lib/prescriptionIntensity";
import { TRAINING_BLOCK_LENGTH_WEEKS } from "@/lib/trainingGoalOptions";
import { ACTIVE_PLAN_CHANGED_EVENT, notifyActivePlanChanged } from "@/lib/activePlanSync";
import { activePlanBlockDates } from "@/lib/activePlanDisplay";

const formatSport = (sportScope) => {
  if (!sportScope) return "Not set";
  const primary = sportScope.primary_sport || "unknown";
  const extra = (sportScope.secondary_sports || []).filter(Boolean);
  if (sportScope.is_multisport || extra.length > 0) {
    return [primary, ...extra].join(" / ");
  }
  return primary;
};

const newClientRequestId = (prefix) => {
  const rand =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `${prefix}-${rand}`;
};

const SESSION_ROLE_LABELS = {
  PRIMARY_KEY: "Key workout",
  SECONDARY_KEY: "Supporting workout",
  LONG_SESSION: "Long session",
  AEROBIC_SUPPORT: "Aerobic",
  RECOVERY: "Recovery",
  MAINTENANCE: "Maintenance",
  TECHNIQUE: "Technique",
  STRENGTH: "Strength",
  BRICK: "Brick",
  BASELINE_COLLECTION: "Baseline",
  OPTIONAL: "Optional",
  REST: "Rest",
};

const sessionRoleLabel = (role) => SESSION_ROLE_LABELS[role] || null;

const WEEKDAY_DISPLAY = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const SPORT_DISPLAY = {
  cycling: "Cycling",
  running: "Running",
  swimming: "Swimming",
  triathlon: "Triathlon",
  duathlon: "Duathlon",
};

const displayWeekday = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return WEEKDAY_DISPLAY[trimmed.toLowerCase()] || trimmed;
};

const readableWorkoutName = (session) => {
  const snapshot = session?.prescription_snapshot;
  const candidates = [
    session?.workout_name,
    session?.name,
    session?.resolved_name,
    snapshot?.name,
    snapshot?.resolved_name,
    snapshot?.workout_name,
  ];
  const name = candidates.find((value) => typeof value === "string" && value.trim());
  return name ? name.trim() : "";
};

const athleteWorkoutTitle = (session) => {
  const readableName = readableWorkoutName(session);
  if (readableName) return readableName;
  const roleLabel = sessionRoleLabel(session.session_role || session.role);
  if (roleLabel) return roleLabel;
  const sport = typeof session.sport === "string" ? session.sport.trim().toLowerCase() : "";
  if (SPORT_DISPLAY[sport]) return `${SPORT_DISPLAY[sport]} workout`;
  return "Workout";
};

const DraftSession = ({ session }) => {
  const effort = prescriptionIntensityLabel(session);
  const roleLabel = sessionRoleLabel(session.session_role || session.role);
  const title = athleteWorkoutTitle(session);
  return (
  <li
    className="app-card-elevated p-3 text-sm min-w-0"
    data-workout-code={session.workout_code || ""}
  >
    <div className="flex flex-wrap items-center gap-2">
      <span className="meta-text">{displayWeekday(session.weekday)}</span>
      {session.scheduled_date && <span className="meta-text">{session.scheduled_date}</span>}
      {session.sport && <SportBadge sport={session.sport} />}
      {roleLabel && (
        <span className="meta-text" data-testid="draft-session-role">{roleLabel}</span>
      )}
    </div>
    <p className="mt-1 text-dark-text-primary" data-testid="draft-session-name">{title}</p>
    <div className="text-dark-text-primary">
      {session.duration_minutes} min
      {effort ? (
        <span data-testid="draft-session-intensity">{` · ${effort}`}</span>
      ) : null}
    </div>
    {session.session_rationale && (
      <p className="mt-2 text-dark-text-secondary text-xs leading-relaxed">{session.session_rationale}</p>
    )}
  </li>
  );
};

export const TrainingPlanDraftReview = ({ draft, draftRef, weekSelector = false, compact = false }) => {
  const weeks = draft?.weeks || [];
  const [selectedWeek, setSelectedWeek] = useState(weeks[0]?.week_index || 1);
  if (!draft) return null;
  const visibleWeeks = weekSelector
    ? weeks.filter((week) => week.week_index === selectedWeek).slice(0, 1)
      || []
    : weeks;
  const shownWeeks = weekSelector
    ? (visibleWeeks.length ? visibleWeeks : weeks.slice(0, 1))
    : weeks;
  const workoutList = shownWeeks.map((week) => (
    <section key={week.week_index} className="app-card-elevated p-3 space-y-2" data-testid={`draft-week-${week.week_index}`}>
      <h3 className="section-title text-sm">
        Week {week.week_index}
        {week.focus_purpose ? ` — ${week.focus_purpose}` : ""}
      </h3>
      <ul className="space-y-2">
        {(week.sessions || []).map((session, idx) => (
          <DraftSession key={`${week.week_index}-${idx}`} session={session} />
        ))}
      </ul>
    </section>
  ));
  return (
    <div
      className={compact ? "mt-3 space-y-3" : "mt-6 space-y-4"}
      data-testid="training-plan-draft-review"
      data-draft-ref={draftRef || ""}
    >
      <div
        className="app-badge border-analytics-amber text-analytics-amber bg-analytics-amber/10 px-3 py-2 text-xs uppercase tracking-widest"
        data-testid="training-plan-draft-banner"
      >
        Draft — not active
      </div>
      {!compact && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CoachingMetric label="Goal" value={draft.goal_reference?.summary || "Not specified"} testId="draft-goal" />
        <CoachingMetric label="Sport" value={formatSport(draft.sport_scope)} testId="draft-sport" />
        <CoachingMetric label="Plan start" value={draft.block_start_date || "Not set"} testId="draft-start-date" />
        <CoachingMetric label="Horizon" value={`${draft.horizon_weeks} week${draft.horizon_weeks === 1 ? "" : "s"}`} testId="draft-horizon" />
      </div>
      )}
      {weekSelector && weeks.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Preview week">
          {weeks.map((week) => (
            <button
              key={week.week_index}
              type="button"
              aria-pressed={week.week_index === (shownWeeks[0]?.week_index)}
              className={`app-badge cursor-pointer ${
                week.week_index === shownWeeks[0]?.week_index
                  ? "border-brand-primary text-brand-primary"
                  : "border-[var(--border-primary)] text-dark-text-secondary"
              }`}
              data-testid={`draft-week-select-${week.week_index}`}
              onClick={() => setSelectedWeek(week.week_index)}
            >
              Week {week.week_index}
            </button>
          ))}
        </div>
      )}
      {compact ? (
        <div
          className="max-h-80 overflow-y-auto space-y-2 pr-1"
          data-testid="training-plan-workout-scroll"
          aria-label="Workout preview"
        >
          {workoutList}
        </div>
      ) : workoutList}
      {(draft.assumptions || []).length > 0 && (
        <div>
          <h3 className="font-heading uppercase tracking-wider text-sm">Assumptions</h3>
          <ul className="mt-2 list-disc pl-5 text-sm text-dark-text-secondary">
            {draft.assumptions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      {(draft.missing_information || []).length > 0 && (
        <div>
          <h3 className="font-heading uppercase tracking-wider text-sm">Missing information</h3>
          <ul className="mt-2 list-disc pl-5 text-sm text-dark-text-secondary">
            {draft.missing_information.map((item) => (
              <li key={item}>{describeMissingInformation(item).message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const formatActivePlanSport = (plan) => {
  if (!plan) return "Not set";
  if (plan.primary_sport === "multisport"
    || (plan.primary_sports || []).length > 1) {
    return (plan.primary_sports || []).filter(Boolean).join(" / ") || "Multisport";
  }
  return plan.primary_sport
    || (plan.primary_sports || [])[0]
    || "Not set";
};

const ActiveTrainingPlanView = ({ plan, goalSummary }) => {
  if (!plan) return null;
  const weeks = plan.weeks || [];
  const currentWeekNumber = plan.current_week_number ?? (weeks[0]?.week_number ?? 1);
  const currentWeek =
    weeks.find((w) => w.week_number === currentWeekNumber) || weeks[0] || null;
  const startDate = plan.block_start_date || "Not set";
  return (
    <div className="mt-6 space-y-4" data-testid="active-training-plan">
      <div
        className="app-badge border-analytics-green text-analytics-green bg-analytics-green/10 px-3 py-2 text-xs uppercase tracking-widest"
        data-testid="active-plan-banner"
      >
        Plan saved
      </div>
      <p className="text-sm text-dark-text-secondary" data-testid="active-plan-schedule-once">
        Workouts are listed once, in Active training plan.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CoachingMetric label="Goal" value={goalSummary || "Not specified"} testId="active-goal" />
        <CoachingMetric label="Sport" value={formatActivePlanSport(plan)} testId="active-sport" />
        <CoachingMetric label="Plan start" value={startDate} testId="active-start-date" />
        <CoachingMetric label="Plan end" value={plan.block_end_date || "Not set"} testId="active-end-date" />
        <CoachingMetric
          label="Last workout"
          value={activePlanBlockDates(plan).finalWorkoutDate || "Not set"}
          testId="active-last-workout"
        />
        <CoachingMetric
          label="Current week"
          value={currentWeek?.week_number != null ? `Week ${currentWeek.week_number}` : "Week 1"}
          testId="active-current-week"
        />
      </div>
    </div>
  );
};

export const TrainingPlanDraftPanel = ({
  goal,
  // Optional VALIDATED_DRAFT payload handed down from TrainingPlanSetup so
  // a freshly-generated plan appears immediately after setup closes —
  // without a page reload. The panel still reconciles with the server
  // lifecycle in its mount effect below.
  seededDraft = null,
  onSeededDraftConsumed = null,
  onPlanChanged = null,
  generatePlan = generateTrainingPlan,
  confirmDraft = confirmTrainingPlanDraft,
  materialiseConfirmation = materialiseTrainingPlanConfirmation,
  activateMaterialisation = activateTrainingPlanMaterialisation,
  replaceActivePlan = replaceActiveTrainingPlan,
  loadCurrentBlock = fetchCurrentTrainingBlock,
  loadBlockWeeks = fetchTrainingBlockWeeks,
  loadCurrentDraft = fetchCurrentTrainingPlanDraft,
  loadCurrentLifecycle = fetchCurrentTrainingPlanLifecycle,
  loadActiveTrainingPlan = fetchCurrentActiveTrainingPlan,
}) => {
  const [horizonWeeks, setHorizonWeeks] = useState(4);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(seededDraft || null);

  const [confirmation, setConfirmation] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const [activating, setActivating] = useState(false);
  const [activation, setActivation] = useState(null);
  const [activationError, setActivationError] = useState(null);
  // Resumed materialisation restored from the lifecycle endpoint after a
  // page refresh. When present with status=MATERIALISED, runActivation
  // reuses this materialisation_ref instead of calling materialise again.
  const [resumedMaterialisation, setResumedMaterialisation] = useState(null);

  const [existingActive, setExistingActive] = useState(null);
  const [replaceRequested, setReplaceRequested] = useState(false);

  // Athlete-facing active-plan projection returned by
  // GET /api/training-block/current/active-plan. Consumed by
  // ActiveTrainingPlanView; assembled server-side from the current training
  // block + linked calendar schedules, so we do NOT read raw block/week
  // shapes on the client.
  const [activePlan, setActivePlan] = useState(null);
  const [activatedGoalSummary, setActivatedGoalSummary] = useState(null);
  const reloadActivePlan = useCallback(async () => {
    try {
      const plan = await loadActiveTrainingPlan();
      if (plan) setActivePlan(plan);
    } catch (_err) { /* non-fatal — banner surfaces auth issues */ }
  }, [loadActiveTrainingPlan]);

  useEffect(() => {
    const onPlanChanged = () => { reloadActivePlan(); };
    window.addEventListener(ACTIVE_PLAN_CHANGED_EVENT, onPlanChanged);
    return () => window.removeEventListener(ACTIVE_PLAN_CHANGED_EVENT, onPlanChanged);
  }, [reloadActivePlan]);

  // Explicit lifecycle hydration gate. While `lifecycleLoading` is true, the
  // panel MUST NOT render Generate/Confirm/Activate/Replace controls — the
  // athlete could otherwise click an action while only part of the lifecycle
  // has been restored, causing e.g. a duplicate materialise. Once the mount
  // effect finishes (success or hard-failure), we flip to loaded=true and
  // the derived lifecycle state below decides which single action is safe.
  const [lifecycleLoading, setLifecycleLoading] = useState(true);
  const [lifecycleError, setLifecycleError] = useState(null);
  // Set to true when the server payload is internally inconsistent (e.g.
  // confirmation.materialisation_status = MATERIALISED but the materialisation
  // object itself is missing). We MUST NOT silently fall back to another
  // materialise attempt; we fail closed and ask the athlete to refresh.
  const [lifecycleInconsistent, setLifecycleInconsistent] = useState(false);

  const clearDownstreamPlanState = useCallback(() => {
    setConfirmation(null);
    setConfirmError(null);
    setActivation(null);
    setActivationError(null);
    setExistingActive(null);
    setReplaceRequested(false);
    setActivePlan(null);
    setResumedMaterialisation(null);
  }, []);

  // When a fresh VALIDATED_DRAFT is handed down from TrainingPlanSetup,
  // seed local state so the athlete sees Confirm/Activate immediately.
  // We also trigger a server-side lifecycle refetch so any downstream
  // records (confirmation/materialisation/activation) reconcile with
  // the seed. This intentionally does NOT rely on a browser refresh or
  // arbitrary setTimeout.
  //
  // Important: an athlete may already have an ACTIVE plan hydrated from
  // the mount lifecycle effect. We must clear that stale activation
  // state here; otherwise the draft UI (which requires !activation)
  // never appears after setup closes.
  useEffect(() => {
    if (!seededDraft || seededDraft.status !== "VALIDATED_DRAFT") return;
    clearDownstreamPlanState();
    setError(null);
    setLifecycleInconsistent(false);
    setResponse(seededDraft);
    // Best-effort reconciliation. Any error is non-fatal because the
    // seed already represents a valid draft.
    (async () => {
      try {
        const lifecycle = await loadCurrentLifecycle();
        if (lifecycle?.draft?.status === "VALIDATED_DRAFT") {
          setResponse(lifecycle.draft);
        }
      } catch { /* non-fatal */ }
      if (typeof onSeededDraftConsumed === "function") {
        onSeededDraftConsumed();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededDraft?.draft_ref || seededDraft?.plan_ref || seededDraft?.status]);

  const summary = useMemo(() => {
    if (!goal) {
      return {
        sport: "Not set yet",
        goalText: "Set your training goal first",
        eventDate: null,
      };
    }
    const sport = goal.sport
      || (goal.sports || []).join(" / ")
      || "Not set";
    return {
      sport,
      goalText: goal.original_text || goal.goal_category,
      eventDate: goal.target_date || goal.effective_target_date || null,
    };
  }, [goal]);

  const missing = (response?.missing_information || []).map((code) => ({
    code,
    ...describeMissingInformation(code),
  }));

  const draftRef = response?.draft_ref || null;
  const draft = response?.draft || null;

  const handleGenerate = async (event) => {
    event.preventDefault();
    setError(null);
    setResponse(null);
    clearDownstreamPlanState();
    setLifecycleInconsistent(false);
    setLifecycleError(null);
    setLoading(true);
    try {
      const result = await generatePlan({ horizonWeeks: Number(horizonWeeks) || 4 });
      setResponse(result);
    } catch (err) {
      setError(err?.planError?.message || err?.message || "Could not generate a training plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!draftRef || confirming) return;
    setConfirmError(null);
    setConfirming(true);
    try {
      const result = await confirmDraft(draftRef, {
        clientRequestId: newClientRequestId("confirm"),
        expectedCandidateId: draft?.candidate_id,
      });
      setConfirmation(result);
    } catch (err) {
      setConfirmError(
        err?.planError?.message
          || err?.message
          || "Could not confirm this draft.",
      );
    } finally {
      setConfirming(false);
    }
  };

  const runActivation = async ({ replace } = { replace: false }) => {
    if (!confirmation?.confirmation_ref) return;
    setActivationError(null);
    setActivating(true);
    try {
      // Resume rule: if the athlete already has a MATERIALISED materialisation
      // (typically restored from the lifecycle endpoint after a page refresh),
      // reuse its materialisation_ref instead of calling materialise again.
      // Re-materialising is not safe here — it would create a duplicate
      // training-block artifact and can itself fail context freshness.
      let materialisation = null;
      if (
        resumedMaterialisation?.materialisation_ref
        && resumedMaterialisation.status === "MATERIALISED"
      ) {
        materialisation = resumedMaterialisation;
      } else {
        materialisation = await materialiseConfirmation(
          confirmation.confirmation_ref,
          { clientRequestId: newClientRequestId("materialise") },
        );
      }
      if (!materialisation?.materialisation_ref) {
        throw new Error("Materialisation did not return a reference.");
      }
      let activationResult;
      if (replace) {
        activationResult = await replaceActivePlan(
          materialisation.materialisation_ref,
          {
            clientRequestId: newClientRequestId("replace"),
            expectedCurrentBlockId: existingActive?.block_id,
          },
        );
      } else {
        activationResult = await activateMaterialisation(
          materialisation.materialisation_ref,
          { clientRequestId: newClientRequestId("activate") },
        );
      }
      setActivatedGoalSummary(
        draft?.goal_reference?.summary || summary.goalText || null,
      );
      setActivation(activationResult);
      setConfirmation(null);
      setResponse(null);
      setResumedMaterialisation(null);
      setConfirmError(null);
      setActivationError(null);
      if (activationResult?.active_plan) {
        setActivePlan(activationResult.active_plan);
        void loadActiveTrainingPlan()
          .then((plan) => {
            if (plan) setActivePlan(plan);
          })
          .catch(() => {});
      } else {
        try {
          const plan = await loadActiveTrainingPlan();
          if (plan) setActivePlan(plan);
        } catch {
          // Non-fatal: banner still renders; the projection can be reloaded
          // on next page load.
        }
      }
      setExistingActive(null);
      setReplaceRequested(false);
      notifyActivePlanChanged({ source: "draft-panel-activation" });
      if (typeof onPlanChanged === "function") {
        void onPlanChanged();
      }
    } catch (err) {
      const code = err?.planError?.code || err?.response?.data?.detail?.code;
      if (code === "active_plan_exists") {
        try {
          const current = await loadCurrentBlock();
          setExistingActive(current);
        } catch {
          // fall through
        }
        setActivationError(
          "You already have an active training plan.",
        );
      } else {
        setActivationError(
          err?.planError?.message
            || err?.message
            || "Could not activate the plan.",
        );
      }
    } finally {
      setActivating(false);
    }
  };

  const handleActivateClick = async () => {
    setActivationError(null);
    try {
      const current = await loadCurrentBlock();
      if (current) {
        setExistingActive(current);
        return;
      }
    } catch {
      // ignore — fall through to attempt activation which will surface a real error
    }
    await runActivation({ replace: false });
  };

  const handleReplaceClick = async () => {
    setReplaceRequested(true);
    await runActivation({ replace: true });
  };

  useEffect(() => {
    // Reset transient replacement warning when the confirmation changes.
    setExistingActive(null);
    setReplaceRequested(false);
  }, [confirmation?.confirmation_ref]);

  useEffect(() => {
    // Atomically restore the athlete's furthest completed lifecycle state
    // after a page refresh. We deliberately hold `lifecycleLoading=true`
    // until every state slot (response/confirmation/resumedMaterialisation/
    // activation + optional active-block fetch) has been applied, so the
    // rendered action button is always consistent with the derived state.
    //
    // Read-only: never triggers confirm/materialise/activate/replace.
    //
    // Inconsistency (confirmation.materialisation_status=MATERIALISED but
    // lifecycle.materialisation missing or non-MATERIALISED) fails closed:
    // we surface a specific error and expose no action button. This is
    // deliberately safer than silently re-materialising, which would create
    // a duplicate training-block artifact.
    let cancelled = false;
    (async () => {
      let lifecycle = null;
      try {
        lifecycle = await loadCurrentLifecycle();
      } catch {
        lifecycle = null;
      }
      if (cancelled) return;

      // No lifecycle payload at all → try the legacy draft-only endpoint
      // as a defensive fallback (older backend without the lifecycle route).
      if (!lifecycle) {
        try {
          const existing = await loadCurrentDraft();
          if (!cancelled && existing && existing.status === "VALIDATED_DRAFT") {
            setResponse((current) => (current == null ? existing : current));
          }
        } catch {
          // ignore — athlete can always click Generate again
        }
        if (!cancelled) setLifecycleLoading(false);
        return;
      }

      // Detect server-side inconsistency BEFORE applying any state so we
      // never render Activate against a missing materialisation ref.
      const claimsMaterialised =
        lifecycle.confirmation?.materialisation_status === "MATERIALISED";
      const hasUsableMaterialisation =
        !!(lifecycle.materialisation
           && lifecycle.materialisation.status === "MATERIALISED"
           && lifecycle.materialisation.materialisation_ref);
      const isActive = lifecycle.activation?.status === "ACTIVE";
      if (claimsMaterialised && !hasUsableMaterialisation && !isActive) {
        // eslint-disable-next-line no-console
        console.error(
          "[training-plan] lifecycle inconsistency: confirmation claims MATERIALISED "
          + "but materialisation is missing/invalid",
          {
            confirmation_ref: lifecycle.confirmation?.confirmation_ref,
            materialisation: lifecycle.materialisation,
          },
        );
        if (lifecycle.draft?.status === "VALIDATED_DRAFT") {
          setResponse((current) => (current == null ? lifecycle.draft : current));
        }
        setLifecycleInconsistent(true);
        setLifecycleLoading(false);
        return;
      }

      if (lifecycle.draft?.status === "VALIDATED_DRAFT") {
        setResponse((current) => (current == null ? lifecycle.draft : current));
      }

      // ACTIVE → jump straight to the active plan view. The projection is
      // assembled server-side from the current training block + linked
      // calendar schedules.
      if (isActive) {
        setActivation(lifecycle.activation);
        try {
          const plan = await loadActiveTrainingPlan();
          if (cancelled) return;
          if (plan) setActivePlan(plan);
        } catch {
          // fall through — active banner still renders from `activation`
        }
        if (!cancelled) setLifecycleLoading(false);
        return;
      }

      // CONFIRMED (with or without a materialisation) → restore Activate.
      if (lifecycle.confirmation && lifecycle.draft) {
        setConfirmation((current) =>
          current == null
            ? {
              confirmation_ref: lifecycle.confirmation.confirmation_ref,
              draft_ref: lifecycle.draft.draft_ref,
              status: lifecycle.confirmation.status,
              materialisation_status: lifecycle.confirmation.materialisation_status,
              material_action_taken: false,
            }
            : current,
        );
      }

      // Resume rule: keep the existing MATERIALISED materialisation so
      // runActivation reuses its ref instead of calling materialise again.
      if (hasUsableMaterialisation) {
        setResumedMaterialisation((current) =>
          current == null ? lifecycle.materialisation : current,
        );
      }

      if (!cancelled) setLifecycleLoading(false);
    })().catch((err) => {
      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.error("[training-plan] lifecycle hydration failed", err);
      setLifecycleError("Could not load training plan state.");
      setLifecycleLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // Intentionally run only on mount / when the resolver identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCurrentLifecycle, loadCurrentDraft, loadActiveTrainingPlan]);

  // Authoritative derived lifecycle state. Priority:
  //   ACTIVE > MATERIALISED > CONFIRMED > DRAFT > NONE
  // Any consumer that needs to know "what can the athlete do next" reads
  // this instead of interpreting individual state slots independently.
  const lifecycleState = useMemo(() => {
    if (lifecycleInconsistent) return "INCONSISTENT";
    if (activation?.status === "ACTIVE") return "ACTIVE";
    if (
      confirmation
      && resumedMaterialisation?.status === "MATERIALISED"
      && resumedMaterialisation?.materialisation_ref
    ) {
      return "MATERIALISED";
    }
    if (confirmation) return "CONFIRMED";
    if (response?.status === "VALIDATED_DRAFT") return "DRAFT";
    return "NONE";
  }, [
    lifecycleInconsistent,
    activation?.status,
    confirmation,
    resumedMaterialisation?.status,
    resumedMaterialisation?.materialisation_ref,
    response?.status,
  ]);

  return (
    <CoachingSectionCard
      title="Training plan"
      subtitle="Generate a reviewable draft"
      headingId="training-plan-heading"
      testId="training-plan-panel"
    >
      <div id="training-plan" className="scroll-mt-24" />

      {lifecycleLoading && (
        <div
          className="flex items-center gap-2 text-sm text-dark-text-secondary"
          data-testid="training-plan-lifecycle-loading"
          role="status"
        >
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Loading training plan state…
        </div>
      )}

      {!lifecycleLoading && lifecycleInconsistent && (
        <div
          role="alert"
          className="border border-analytics-amber text-analytics-amber bg-analytics-amber/10 rounded-md px-3 py-2 text-sm"
          data-testid="training-plan-lifecycle-inconsistent"
        >
          Your training plan is already prepared, but its activation record
          could not be loaded. Please refresh the page.
        </div>
      )}

      {!lifecycleLoading && lifecycleError && !lifecycleInconsistent && (
        <p
          role="alert"
          className="text-sm text-analytics-amber"
          data-testid="training-plan-lifecycle-error"
        >
          {lifecycleError}
        </p>
      )}

      {!lifecycleLoading && !lifecycleInconsistent && (
      <form onSubmit={handleGenerate} className="space-y-4" data-testid="training-plan-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <CoachingMetric label="Sport" value={summary.sport} testId="plan-preview-sport" />
          <CoachingMetric label="Primary goal" value={summary.goalText} testId="plan-preview-goal" />
          <CoachingMetric label="Target event / date" value={summary.eventDate || "No event date"} testId="plan-preview-event" />
        </div>
        <label className="block">
          <span className="card-label">
            This training block (weeks)
          </span>
          <select
            className="app-control mt-2 text-sm"
            value={horizonWeeks}
            onChange={(event) => setHorizonWeeks(event.target.value)}
            data-testid="plan-horizon"
          >
            {TRAINING_BLOCK_LENGTH_WEEKS.map((weeks) => (
              <option key={weeks} value={weeks}>
                {weeks} weeks
              </option>
            ))}
          </select>
          <span className="mt-1 block meta-text">
            Length of the next detailed block, not the whole goal deadline.
          </span>
        </label>
        <Button
          type="submit"
          disabled={loading || !goal}
          data-testid="generate-training-plan"
          className="btn-primary-app"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Generating…
            </>
          ) : (
            "Generate training plan"
          )}
        </Button>
      </form>
      )}

      {!lifecycleLoading && !lifecycleInconsistent && error && (
        <p role="alert" data-testid="training-plan-error" className="mt-4 text-sm text-analytics-amber">
          {error}
        </p>
      )}

      {!lifecycleLoading && !lifecycleInconsistent && response?.status === "NEEDS_INFORMATION" && (
        <div className="mt-4 space-y-2" data-testid="training-plan-needs-information">
          <p className="text-sm text-dark-text-primary">
            I need a bit more information before I can draft a training plan.
          </p>
          <ul className="space-y-2">
            {missing.map((item) => (
              <li key={item.code} className="text-sm">
                <span data-testid="missing-info-message">{item.message}</span>
                {item.href && (
                  <a
                    href={item.href}
                    className="ml-3 inline-flex text-[11px] uppercase tracking-widest border border-current px-2 py-1 rounded"
                    data-testid="set-training-goal-action"
                  >
                    {item.actionLabel}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!lifecycleLoading && !lifecycleInconsistent && response?.status === "VALIDATED_DRAFT" && !confirmation && !activation && (
        <>
          <TrainingPlanDraftReview
            draft={draft}
            draftRef={draftRef}
          />
          {response.material_action_taken === false && (
            <p
              className="mt-3 text-xs uppercase tracking-widest text-dark-text-muted"
              data-testid="draft-not-materialised"
            >
              Not confirmed, not scheduled, not active
            </p>
          )}
          <div className="mt-4 space-y-2" data-testid="training-plan-confirm-section">
            <p className="text-sm text-dark-text-secondary" data-testid="confirm-explanation">
              Confirming approves this draft but does not start the plan yet.
            </p>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={confirming || !draftRef}
              data-testid="confirm-training-plan"
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  Confirming…
                </>
              ) : (
                "Confirm this plan"
              )}
            </Button>
            {confirmError && (
              <p role="alert" className="text-sm text-analytics-amber" data-testid="confirm-error">
                {confirmError}
              </p>
            )}
          </div>
        </>
      )}

      {!lifecycleLoading && !lifecycleInconsistent && confirmation && !activation && (
        <div className="mt-6 space-y-3" data-testid="training-plan-confirmed-section">
          <div
            className="border border-analytics-amber text-analytics-amber bg-analytics-amber/10 rounded-md px-3 py-2 text-xs uppercase tracking-widest"
            data-testid="confirmed-banner"
          >
            Plan confirmed — not active
          </div>
          <p className="text-sm text-dark-text-secondary">
            Activating will schedule the sessions and start the plan. Nothing has been
            scheduled or started yet.
          </p>
          {existingActive ? (
            <div
              role="alert"
              className="border border-analytics-amber text-analytics-amber bg-analytics-amber/10 rounded-md px-3 py-2 text-sm"
              data-testid="active-plan-replacement-warning"
            >
              <p className="font-medium">
                Replace your current training plan with this plan?
              </p>
              <p className="mt-1">
                Your current plan stays active until you confirm. Replacing it
                will stop the current plan and start this one instead.
              </p>
              <Button
                type="button"
                onClick={handleReplaceClick}
                disabled={activating || !existingActive?.block_id}
                data-testid="replace-active-plan"
                className="mt-2"
              >
                {activating && replaceRequested ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Replacing…
                  </>
                ) : (
                  "Replace current plan"
                )}
              </Button>
              {activationError && (
                <p role="alert" className="mt-2 text-sm text-analytics-amber" data-testid="replacement-error">
                  {activationError}
                </p>
              )}
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleActivateClick}
              disabled={activating}
              data-testid="activate-training-plan"
            >
              {activating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  Activating…
                </>
              ) : (
                "Activate training plan"
              )}
            </Button>
          )}
          {activationError && !existingActive && (
            <p role="alert" className="text-sm text-analytics-amber" data-testid="activation-error">
              {activationError}
            </p>
          )}
        </div>
      )}

      {!lifecycleLoading && !lifecycleInconsistent && activation && (
        <ActiveTrainingPlanView
          plan={activePlan}
          goalSummary={activatedGoalSummary || draft?.goal_reference?.summary || summary.goalText}
        />
      )}
    </CoachingSectionCard>
  );
};

export default TrainingPlanDraftPanel;
